// WalkthroughCompanion.jsx — Ambient executive operational telemetry rail.
//
// Slim right-side dock that surfaces walkthrough scorecard signal while
// the director navigates the platform during a demo. Strict visibility
// rules: only renders when
//   (a) user is admin/director,
//   (b) a baseline snapshot is captured (office scenario active),
//   (c) we are NOT already on the Demo Control page (which has the
//       full scorecard inline — the rail would duplicate it).
//
// Discipline:
//   • Restrained motion — single calm opacity-translate fade, no pulses
//   • Maximum 2-sentence narrative, no hype
//   • Read-only — no actions other than collapse / dismiss / export
//   • Polls every 30 s for ambient updates; never websockets
//   • Self-mounts ONCE in App.js; renders null when the rules fail
//     so it costs nothing during normal production use.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ChevronRight, FileDown, Minimize2, Maximize2, X } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../data/authStore";

const POLL_INTERVAL_MS = 30_000;
const DISMISS_KEY = "chainflow_walkthrough_companion_dismissed";

// Map the latest scenario action name → calm display label for the
// status badge. Only office-level scenarios surface here.
const SCENARIO_LABELS = {
  best_month_ever:     "Best Month Ever",
  market_slowdown:     "Market Slowdown",
  chain_crisis:        "Chain Crisis",
  exchange_week_chaos: "Exchange Week Chaos",
};

const PANEL = {
  background:
    "linear-gradient(180deg, rgba(20,22,46,0.86) 0%, rgba(15,17,38,0.86) 100%)",
  border: "1px solid rgba(99,102,241,0.18)",
  backdropFilter: "blur(16px) saturate(140%)",
  WebkitBackdropFilter: "blur(16px) saturate(140%)",
  boxShadow:
    "0 1px 0 rgba(255,255,255,0.04) inset, 0 18px 40px -22px rgba(0,0,0,0.65)",
};

function _formatGBP(n) {
  const v = Number(n) || 0;
  if (v >= 1000) return `£${(v / 1000).toFixed(1)}k`;
  return `£${v}`;
}

function _durationLabel(secs) {
  const s = Math.max(0, Number(secs) || 0);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  return `${Math.round(s / 3600)}h`;
}

function _twoSentence(narrative) {
  // The backend already returns 1–4 calm sentences. We cap at 2 for the
  // rail without rewriting the source — the user can read the full
  // narrative on /settings/demo-control when they need it.
  if (!narrative) return "";
  const parts = narrative.split(/(?<=\.)\s+/);
  return parts.slice(0, 2).join(" ");
}

export default function WalkthroughCompanion() {
  const location = useLocation();
  const { user } = useAuth();
  const [scorecard, setScorecard] = useState(null);
  const [allowed, setAllowed] = useState(false);
  const [checkedGate, setCheckedGate] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return window.sessionStorage.getItem(DISMISS_KEY) === "1";
    } catch (_e) {
      return false;
    }
  });
  const timerRef = useRef(null);

  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

  // Single combined probe — fetching the scorecard implies the gate
  // is open (it's behind `_require_demo_control` on the backend). A 403
  // means the user is in production / non-demo; we stay dormant.
  const fetchScorecard = useCallback(async () => {
    try {
      const { data } = await api.get("/api/demo-control/walkthrough/scorecard");
      setScorecard(data);
      setAllowed(true);
    } catch (_e) {
      setAllowed(false);
    } finally {
      setCheckedGate(true);
    }
  }, []);

  // Fire immediately on auth + every 30 s while the rail is alive.
  useEffect(() => {
    if (!user) { setCheckedGate(true); return; }
    fetchScorecard();
    timerRef.current = window.setInterval(fetchScorecard, POLL_INTERVAL_MS);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [user, fetchScorecard]);

  // Visibility rules: only show when walkthrough is active. Also hide
  // on /settings/demo-control to avoid duplicating the inline scorecard,
  // and respect a user dismissal for the current browser tab.
  const visible = useMemo(() => {
    if (!checkedGate || !allowed) return false;
    if (dismissed) return false;
    if (!scorecard?.active) return false;
    if (location.pathname.startsWith("/settings/demo-control")) return false;
    return true;
  }, [checkedGate, allowed, dismissed, scorecard, location.pathname]);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
    try { window.sessionStorage.setItem(DISMISS_KEY, "1"); } catch (_e) { /* ignore */ }
  }, []);

  if (!visible) return null;

  const lastAction = (scorecard?.operations || []).find(
    (op) => SCENARIO_LABELS[op.action],
  );
  const scenarioLabel = lastAction ? SCENARIO_LABELS[lastAction.action] : "Walkthrough";
  const duration = _durationLabel(scorecard.duration_seconds);
  const m = scorecard.metrics || {};

  // The 4 most-calm telemetry lines. We deliberately omit zero values
  // so the rail never feels noisy — restraint over completeness.
  const telemetry = [
    m.fee_at_risk_recovered_gbp > 0 && {
      label: "Fee-at-risk recovered",
      value: _formatGBP(m.fee_at_risk_recovered_gbp),
    },
    m.confidence_recovered_points > 0 && {
      label: "Confidence recovered",
      value: `${m.confidence_recovered_points} pts`,
    },
    m.transactions_stabilised > 0 && {
      label: "Transactions stabilised",
      value: m.transactions_stabilised,
    },
    m.chains_impacted > 0 && {
      label: "Chains impacted",
      value: m.chains_impacted,
    },
    m.exchange_velocity_days > 0 && {
      label: "Exchange velocity",
      value: `${m.exchange_velocity_days} d forward`,
    },
  ].filter(Boolean).slice(0, 4);

  return (
    <aside
      className="hidden lg:flex fixed top-1/2 -translate-y-1/2 right-3 z-30 flex-col"
      style={{
        width: collapsed ? "44px" : "292px",
        transition: "width 280ms cubic-bezier(0.22, 1, 0.36, 1), opacity 280ms ease",
      }}
      data-testid="walkthrough-companion"
      aria-label="Walkthrough operational telemetry"
    >
      {collapsed ? (
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          className="w-11 h-11 rounded-xl grid place-items-center text-slate-300 hover:text-white"
          style={PANEL}
          data-testid="walkthrough-companion-expand"
          title={`${scenarioLabel} active · ${duration}`}
        >
          <Maximize2 size={14} strokeWidth={1.75} />
        </button>
      ) : (
        <div
          className="rounded-2xl px-4 py-3.5"
          style={PANEL}
          data-testid="walkthrough-companion-panel"
        >
          {/* Header */}
          <div className="flex items-center gap-2 mb-3">
            <span
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9.5px] font-semibold uppercase tracking-[0.16em]"
              style={{
                background: "rgba(139,92,246,0.10)",
                color: "#c4b5fd",
                border: "1px solid rgba(139,92,246,0.30)",
              }}
              data-testid="walkthrough-companion-badge"
            >
              <span className="w-1 h-1 rounded-full" style={{ background: "currentColor" }} />
              {scenarioLabel} active
            </span>
            <span className="ml-auto text-[10.5px] text-slate-500" data-testid="walkthrough-companion-duration">
              {duration}
            </span>
          </div>

          {/* Telemetry rows */}
          <div className="space-y-1.5 mb-3" data-testid="walkthrough-companion-telemetry">
            {telemetry.length === 0 ? (
              <p className="text-[11px] text-slate-500 leading-relaxed">
                Operational profile is holding steady — no material movement yet.
              </p>
            ) : (
              telemetry.map((row) => (
                <div
                  key={row.label}
                  className="flex items-baseline justify-between gap-3 text-[11px]"
                  data-testid={`walkthrough-companion-row-${row.label.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <span className="text-slate-400">{row.label}</span>
                  <span className="text-white font-medium tabular-nums">{row.value}</span>
                </div>
              ))
            )}
          </div>

          {/* Narrative (2 sentence max) */}
          <p
            className="text-[10.5px] text-slate-400 leading-relaxed mb-3 italic"
            data-testid="walkthrough-companion-narrative"
          >
            {_twoSentence(scorecard.narrative)}
          </p>

          {/* Footer controls */}
          <div className="flex items-center gap-2 pt-2.5 border-t border-white/[0.04]">
            <a
              href={`${apiBase}/api/demo-control/walkthrough/export/txt`}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-bold text-slate-400 hover:text-indigo-300 transition-colors"
              data-testid="walkthrough-companion-export"
              title="Export walkthrough snapshot"
            >
              <FileDown size={11} strokeWidth={2} /> Snapshot
            </a>
            <a
              href="/settings/demo-control"
              className="ml-auto inline-flex items-center gap-0.5 text-[10px] uppercase tracking-[0.14em] font-bold text-slate-400 hover:text-indigo-300 transition-colors"
              data-testid="walkthrough-companion-open"
              title="Open Demo Control Centre"
            >
              Open <ChevronRight size={11} strokeWidth={2} />
            </a>
            <button
              type="button"
              onClick={() => setCollapsed(true)}
              className="text-slate-500 hover:text-slate-300 transition-colors"
              data-testid="walkthrough-companion-collapse"
              title="Collapse"
              aria-label="Collapse"
            >
              <Minimize2 size={11} strokeWidth={1.75} />
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              className="text-slate-500 hover:text-slate-300 transition-colors"
              data-testid="walkthrough-companion-dismiss"
              title="Dismiss"
              aria-label="Dismiss"
            >
              <X size={11} strokeWidth={1.75} />
            </button>
          </div>
        </div>
      )}
    </aside>
  );
}
